import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, BedDouble, Bath, MapPin, ShieldCheck, Calendar, Sparkles, CheckCircle2, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { trackFormSubmit, trackPhoneClick } from "../lib/tracking";
import { useSEO, breadcrumbSchema, SITE_URL } from "../lib/seo";

export default function VacancyDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [v, setV] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(0);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", role: "participant", move_in_preference: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    api.get(`/vacancies/${id}`).then((r) => setV(r.data)).catch(() => setV(false)).finally(() => setLoading(false));
  }, [id]);

  // Per-vacancy SEO — dynamic title/description/canonical/schema once the vacancy loads
  useSEO({
    title: v ? `${v.title} — ${v.suburb}, ${v.state} | ${v.service_type} Vacancy | Alpha Community Care` : "NDIS Home Vacancy | Alpha Community Care",
    description: v
      ? `${v.service_type} in ${v.suburb}, ${v.state}. ${v.bedrooms} bed · ${v.bathrooms} bath · ${v.vacant_rooms} vacant. Available from ${v.move_in_date}. Enquire with Alpha Community Care.`
      : "Explore NDIS SIL, SDA, STA and MTA home vacancies across Melbourne, Perth and Australia with Alpha Community Care.",
    keywords: v
      ? [`${v.service_type} ${v.suburb}`, `NDIS home ${v.suburb}`, `${v.service_type} ${v.state}`, "NDIS vacancy", v.sda_category].filter(Boolean)
      : ["NDIS home vacancy", "SIL vacancy Melbourne", "SIL vacancy Perth"],
    canonical: v ? `${SITE_URL}/vacancies/${v.id}` : `${SITE_URL}/vacancies`,
    jsonLd: v ? [
      breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Our homes", path: "/vacancies" },
        { name: v.title, path: `/vacancies/${v.id}` },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "Accommodation",
        "name": v.title,
        "description": v.description,
        "url": `${SITE_URL}/vacancies/${v.id}`,
        "numberOfBedrooms": v.bedrooms,
        "numberOfBathroomsTotal": v.bathrooms,
        "image": [v.hero_image, ...(v.gallery || [])].filter(Boolean),
        "address": {
          "@type": "PostalAddress",
          "addressLocality": v.suburb,
          "addressRegion": v.state,
          "addressCountry": "AU",
        },
        "amenityFeature": (v.accessibility_features || []).map((f) => ({ "@type": "LocationFeatureSpecification", "name": f, "value": true })),
      },
    ] : [],
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone) {
      toast.error("Please complete name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/vacancies/${id}/enquire`, { ...form, vacancy_id: id });
      trackFormSubmit("vacancy_enquiry", { vacancy_id: id, role: form.role });
      toast.success("Enquiry sent. Our team will be in touch within 1 business day.");
      setSubmitted(true);
      setForm({ full_name: "", email: "", phone: "", role: "participant", move_in_preference: "", message: "" });
    } catch (err) {
      toast.error("Could not send enquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="alpha-container py-20 text-center text-[color:var(--alpha-mute)]">Loading…</div>;
  if (!v) return (
    <div className="alpha-container py-20 text-center">
      <div className="font-display text-2xl font-semibold">Home not found</div>
      <button className="alpha-btn-ghost mt-5" onClick={() => nav("/vacancies")}>Back to vacancies</button>
    </div>
  );

  const images = [v.hero_image, ...(v.gallery || [])].filter(Boolean);

  return (
    <div data-testid={`page-vacancy-${v.id}`}>
      <div className="alpha-container pt-6">
        <Link to="/vacancies" className="inline-flex items-center gap-2 text-sm text-[color:var(--alpha-mute)] hover:text-[color:var(--alpha-purple-700)]">
          <ArrowLeft className="w-4 h-4" /> Back to vacancies
        </Link>
      </div>

      {/* Gallery */}
      <section className="alpha-container pt-6" data-testid="vacancy-gallery">
        <div className="grid lg:grid-cols-4 gap-3 h-[420px] lg:h-[520px]">
          <div className="lg:col-span-3 relative rounded-2xl overflow-hidden">
            <img src={images[active]} alt={v.title} className="w-full h-full object-cover" />
            <div className="absolute top-4 left-4 flex gap-2">
              <span className="alpha-chip !bg-white/90">{v.service_type}</span>
              {v.sda_category && <span className="alpha-chip !bg-[#0CA4A5]/15 !text-[#0CA4A5] !border-[#0CA4A5]/20">{v.sda_category}</span>}
            </div>
          </div>
          <div className="hidden lg:grid grid-rows-3 gap-3">
            {images.slice(1, 4).map((img, i) => (
              <button
                key={img + i}
                onClick={() => setActive(i + 1)}
                className={`relative rounded-2xl overflow-hidden group ${active === i + 1 ? "ring-2 ring-[color:var(--alpha-purple-700)]" : ""}`}
                data-testid={`vacancy-thumb-${i}`}
              >
                <img src={img} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="alpha-container py-12 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8">
          <div className="flex items-center gap-2 text-sm text-[color:var(--alpha-mute)]">
            <MapPin className="w-4 h-4" /> {v.suburb}, {v.state}
          </div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-display font-semibold tracking-tight">{v.title}</h1>

          <div className="mt-6 flex flex-wrap gap-6 text-sm text-[color:var(--alpha-ink)]">
            <span className="flex items-center gap-2"><BedDouble className="w-4 h-4 text-[color:var(--alpha-purple-700)]" /> {v.bedrooms} bedrooms</span>
            <span className="flex items-center gap-2"><Bath className="w-4 h-4 text-[color:var(--alpha-purple-700)]" /> {v.bathrooms} bathrooms</span>
            <span className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[color:var(--alpha-purple-700)]" /> {v.vacant_rooms} vacant</span>
            <span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[color:var(--alpha-purple-700)]" /> From {v.move_in_date}</span>
          </div>

          <div className="mt-8 prose max-w-none">
            <p className="text-lg leading-relaxed text-[color:var(--alpha-mute)]">{v.description}</p>
          </div>

          <div className="mt-10 grid sm:grid-cols-2 gap-5">
            <div className="alpha-card p-6">
              <div className="alpha-overline">Home features</div>
              <ul className="mt-3 space-y-2">
                {v.features?.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm"><CheckCircle2 className="w-4 h-4 mt-0.5 text-[color:var(--alpha-purple-700)]" /> {f}</li>
                ))}
              </ul>
            </div>
            <div className="alpha-card p-6">
              <div className="alpha-overline !text-[color:var(--alpha-green-600)]">Accessibility</div>
              <ul className="mt-3 space-y-2">
                {v.accessibility_features?.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm"><ShieldCheck className="w-4 h-4 mt-0.5 text-[color:var(--alpha-purple-700)]" /> {f}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Enquiry form */}
        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-32">
            <div className="alpha-card p-6">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--alpha-green-700)] font-semibold">
                {v.vacant_rooms > 0 ? `${v.vacant_rooms} ${v.vacant_rooms === 1 ? "vacancy" : "vacancies"} available` : "Enquire for capacity"}
              </div>
              <div className="mt-1 font-display text-xl font-semibold">Enquire about this home</div>
              <div className="text-xs text-[color:var(--alpha-mute)] mt-1">SIL support pricing is separate from housing costs and varies by NDIS plan. Our intake team will walk you through it.</div>

              <form onSubmit={submit} className="mt-6 space-y-3" data-testid="vacancy-enquiry-form">
                {submitted && (
                  <div className="p-3 rounded-xl bg-[#E6F4EC] border border-[#CCE5D6] text-[#3F8F5D] text-sm flex items-center gap-2" data-testid="enquiry-success">
                    <CheckCircle2 className="w-4 h-4" /> Enquiry received. We'll be in touch within 1 business day.
                  </div>
                )}
                <Input placeholder="Full name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required data-testid="enquiry-name" />
                <Input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="enquiry-email" />
                <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required data-testid="enquiry-phone" />
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="enquiry-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="participant">Participant</SelectItem>
                    <SelectItem value="family">Family member</SelectItem>
                    <SelectItem value="coordinator">Support coordinator</SelectItem>
                    <SelectItem value="provider">Other provider</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <Input placeholder="Preferred move-in date (optional)" value={form.move_in_preference} onChange={(e) => setForm({ ...form, move_in_preference: e.target.value })} data-testid="enquiry-movein" />
                <Textarea placeholder="Tell us about your needs (optional)" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} data-testid="enquiry-message" />
                <button type="submit" disabled={submitting} className="alpha-btn-primary w-full justify-center" data-testid="enquiry-submit">
                  {submitting ? "Sending…" : <>Enquire about this home <ArrowRight className="w-4 h-4" /></>}
                </button>
                <p className="text-[11px] text-[color:var(--alpha-mute)]">By submitting you agree to our privacy policy. We respond within 1 business day.</p>
              </form>
            </div>

            <div className="alpha-card p-5 mt-4 alpha-glass">
              <div className="text-sm font-semibold">Prefer to talk?</div>
              <a href="tel:1300380692" onClick={() => trackPhoneClick("1300 380 692", "vacancy-detail")} className="font-display text-xl font-semibold alpha-gradient-text">1300 380 692</a>
              <div className="text-xs text-[color:var(--alpha-mute)]">Mon–Fri, national number</div>
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}
